import useConfirmation from '../common/useConfirmation';
import { interfaceError } from '../../i18n/errorMessages';
import { message as interfaceMessage } from '../../i18n/messages';
import LocalizedMessage from '../common/LocalizedMessage';
import React, {useEffect, useMemo, useRef, useState} from "react";
import toast from '../../i18n/toast';
import 'react-toastify/dist/ReactToastify.css';
import { useHistory, useLocation } from 'react-router-dom';
import {connect} from "react-redux";
import * as PropTypes from "prop-types";
import API from "../../services/SchoolClassService";
import SettingsService from "../../services/SettingsService";
import SchoolClassForm from "./SchoolClassForm";
import { isValidDateValue } from "../common/PolishDateInput";
import {ROLES, STATUS, WEEKDAYS} from "../common/Const";
import {
  DEFAULT_LESSON_TYPES_SETTING,
  getConfiguredLessonTypeOptions,
  getLessonTypeById,
  getLessonTypesSetting,
} from "../common/lessonTypes";
import {mandatory, validateValues} from "../common/Validations";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import * as teacherActions from "../../redux/actions/teacherActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as bookActions from "../../redux/actions/bookActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as clientActions from "../../redux/actions/clientActions";
import {
  findDayOfWeek,
  getById,
  hasErrors,
  hasNoErrors,
  minutesToTimeString,
  timeStringToMinutes,
  getStudentsInThisClass,
} from "../common/Helpers";
import LessonsList from "./LessonsList";
import StudentsList from "./StudentsList";
import ClassStudentPickerModal from "./ClassStudentPickerModal";
import {
  findOverlapSchoolClass as findOverlappingSchoolClass,
  saveSchoolClassWithAttendanceGuard,
} from "./schoolClassScheduling";
import {
  buildManageRouteWithSchoolYear,
} from "./manageSchoolYearRouting";
import { buildSchoolYearRange } from "../common/schoolYear";
import { canManageClasses, getEffectivePermissionRole } from "./rbac";
import { buildDashboardReturnRoute, getPrincipalKey, readCreationContext } from './dashboardClassCreationNavigation';
import { useTranslation } from 'react-i18next';

const EMPTY_SCHOOL_CLASS = { isRecurring: true };

const validationConfig = {
  classTypeId: {
    label: "Typ zajęć",
    validate: [mandatory],
  },
  dayOfWeek: {
    label: "Dzień tygodnia",
    validate: [mandatory],
  },
  startTime: {
    label: "Początek zajęć",
    validate: [mandatory],
  },
  teacherId: {
    label: "Nauczyciel",
    validate: [mandatory],
  },
};

function getName(person) {
  return person ? `${person.firstName || ''} ${person.lastName || ''}` : '';
}

export function SchoolClass({
  schoolClasses, loadSchoolClasses, teachers, loadTeachers, saveSchoolClass, scheduledLessons,
  students, loadStudents, loadClassStudents, studentsInThisClass, studentsNotInThisClass,
  addClassStudent, removeClassStudent, saveLessonRegister, loadLessonRegisters, deleteLessonRegister, books, loadBooks,
  clients, loadClients,
  activeSchoolYearStartYear,
  userRole,
  userTeacherId,
  getSettings,
  principalKey,
  ...props
}) {
  const { confirm, confirmation } = useConfirmation();
  const { t: tr } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const isNewClass = props.match.params.id === 'new';
  const creationContext = useMemo(() => (
    isNewClass && canManageClasses(userRole) ? readCreationContext(location.state, principalKey) : null
  ), [isNewClass, location.state, principalKey, userRole]);
  const draftKey = `${location.key || ''}:${principalKey}`;
  const initializedDraft = useRef(null);
  const saving = useRef(false);
  const activePrincipal = useRef(principalKey);
  activePrincipal.current = principalKey;
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const activeSchoolYearRange = buildSchoolYearRange(activeSchoolYearStartYear);
  const isTeacher = userRole === ROLES.TEACHER;
  const canManageClass = !isTeacher;
  // eslint-disable-next-line react/destructuring-assignment
  const [schoolClass, setSchoolClass] = useState({ ...props.schoolClass, ...(creationContext ? creationContext.prefill : {}) });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState(STATUS.VIEW);
  const [showSelectStudentModal, setShowSelectStudentModal] = useState(false);
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [addStudentError, setAddStudentError] = useState("");
  const addingStudentRef = useRef(false);
  const [dateReadOnly, setDateReadOnly] = useState(false);
  const [classTypes, setClassTypes] = useState(getConfiguredLessonTypeOptions([], schoolClass.classTypeId));
  const [lessonTypes, setLessonTypes] = useState(DEFAULT_LESSON_TYPES_SETTING);


  useEffect(() => {
    Promise.resolve()
      .then(() => getSettings())
      .then((settings) => {
        setClassTypes(getConfiguredLessonTypeOptions(settings, schoolClass.classTypeId));
        setLessonTypes(getLessonTypesSetting(settings));
      })
      .catch(() => {
        setClassTypes(getConfiguredLessonTypeOptions([], schoolClass.classTypeId));
        setLessonTypes(DEFAULT_LESSON_TYPES_SETTING);
      });
  }, [getSettings, schoolClass.classTypeId]);

  useEffect(() => {
    loadSchoolClasses(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      toast.error(interfaceError(error, 'common:errors.load'));
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadSchoolClasses]);

  useEffect(() => {
    if (isNewClass) {
      if (initializedDraft.current !== draftKey) {
        saving.current = false;
        setSchoolClass({ ...EMPTY_SCHOOL_CLASS, ...(creationContext ? creationContext.prefill : {}) });
        setErrors({});
        setTouched({});
        setStatus(STATUS.IDLE);
        initializedDraft.current = draftKey;
        if (creationContext) window.scrollTo(0, 0);
      }
      return;
    }
    setSchoolClass({...props.schoolClass});
    if (props.schoolClass.id) {
      setStatus(STATUS.VIEW);
    } else {
      setStatus(STATUS.IDLE);
    }
    // eslint-disable-next-line react/destructuring-assignment
  }, [props.schoolClass, isNewClass, draftKey, creationContext]);

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers().catch((error) => {
        toast.error(interfaceError(error, 'common:errors.load'));
      });
    }
  }, []);

  useEffect(() => {
    loadClassStudents(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      toast.error(interfaceError(error, 'common:errors.load'));
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadClassStudents]);

  useEffect(() => {
    if (isTeacher || students.length === 0) {
      loadStudents().catch((error) => {
        toast.error(interfaceError(error, 'common:errors.load'));
      });
    }
  }, [isTeacher, loadStudents]);

  useEffect(() => {
    if (canManageClass && books.length === 0) {
      loadBooks().catch((error) => {
        toast.error(interfaceError(error, 'common:errors.load'));
      });
    }
  }, [books.length, canManageClass, loadBooks]);

  useEffect(() => {
    loadLessonRegisters(activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate).catch((error) => {
      toast.error(interfaceError(error, 'common:errors.load'));
    });
  }, [activeSchoolYearRange.fromDate, activeSchoolYearRange.tillDate, loadLessonRegisters]);

  useEffect(() => {
    if (canManageClass && clients.length === 0) {
      loadClients().catch((error) => {
        toast.error(interfaceError(error, 'common:errors.load'));
      });
    }
  }, [canManageClass, clients.length, loadClients]);

  useEffect(() => {
    let hasNonEmptyLessonRegister = false;

    scheduledLessons.forEach((lesson) => {
      if (lesson.lessonRegister !== null) {
        hasNonEmptyLessonRegister = true;
      }
    });

    setDateReadOnly(hasNonEmptyLessonRegister);
  }, [scheduledLessons]);

  function getSchoolClassName(aSchoolClass) {
    const {startTime, dayOfWeek, classTypeId} = aSchoolClass;
    const dayName = findDayOfWeek(dayOfWeek).name;
    const selectedClassType = getLessonTypeById(lessonTypes, classTypeId);
    const startTimeMinutes = timeStringToMinutes(startTime);
    const endTime = (selectedClassType && startTimeMinutes)
      ? minutesToTimeString(startTimeMinutes + selectedClassType.durationMinutes)
      : "";
    return `${dayName} ${selectedClassType ? selectedClassType.name : classTypeId} ${startTime}-${endTime}`;
  }

  function findOverlapSchoolClass() {
    return findOverlappingSchoolClass(schoolClass, schoolClasses, undefined, lessonTypes);
  }

  const validate = () => {
    const {isRecurring, dayOfWeek, fromDate, tillDate} = schoolClass;
    const fromDateDayOfWeek = WEEKDAYS.find(d => d.day === (new Date(fromDate)).getDay());
    // eslint-disable-next-line no-shadow
    const errors = validateValues(schoolClass, validationConfig);
    if (!fromDate) {
      errors.fromDate = tr("manage:thisFieldIsRequired_287");
    }
    if (fromDate && !isValidDateValue(fromDate)) errors.fromDate = tr("manage:enterAValidDateInDDMMYYYYFormat_205");
    if (isRecurring && tillDate && !isValidDateValue(tillDate)) errors.tillDate = tr("manage:enterAValidDateInDDMMYYYYFormat_205");
    if (!isRecurring && dayOfWeek && fromDate && (!fromDateDayOfWeek || fromDateDayOfWeek.id !== dayOfWeek)) {
      errors.dayOfWeek = tr("manage:theDayOfTheWeekDoesNotMatchThe_288");
    }
    if (isRecurring && !tillDate) {
      errors.tillDate = tr("manage:thisFieldIsRequired_287");
    }
    if (isRecurring && isValidDateValue(fromDate) && isValidDateValue(tillDate) && fromDate > tillDate) {
      errors.fromDate = tr("manage:theClassEndDateCannotBeEarlierThanThe_289");
      errors.tillDate = tr("manage:theClassEndDateCannotBeEarlierThanThe_289");
    }
    // check teacher does not have any other class at the same time
    const overlapped = !errors.fromDate && !errors.tillDate ? findOverlapSchoolClass() : null;
    if (overlapped) {
      const message = interfaceMessage('manage:classOverlap', { name: getSchoolClassName(overlapped) });
      errors.saveError = message;
    }
    // check that if class is not recurrent than fromDate must be equal to tillDate
    // check that if class is  recurrent than fromDate must be before tillDate and both must be populated
    setErrors(errors);
    return hasNoErrors(errors);
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched({ ...touched, [name]: true });
    validate();
  };


  const handleCancel = () => {
    if (saving.current) return;
    if (creationContext) {
      history.replace(buildDashboardReturnRoute(creationContext, location.search));
      return;
    }
    if (status === STATUS.VIEW || !schoolClass.id) {
      const fallbackPath = (location.state && location.state.from) || '/manage/classes';
      history.push(buildManageRouteWithSchoolYear(fallbackPath, location.search, activeSchoolYearStartYear));
    } else {
      setSchoolClass(props.schoolClass);
      setStatus(STATUS.VIEW);
    }
  };

  // const handleCancel = () => {
  //   if (status === STATUS.VIEW || !schoolClass.id) {
  //     history.push('/manage/classes');
  //   } else {
  //     setSchoolClass(props.schoolClass);
  //     setStatus(STATUS.VIEW);
  //   }
  // };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setSchoolClass(prevClass => ({...prevClass, [name]: value}));
    validate();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving.current || (isNewClass && !canManageClasses(userRole))) return;
    saving.current = true;
    setStatus(STATUS.SUBMITTING);
    const isValid = validate();

    if (isValid) {
      // eslint-disable-next-line no-alert
      const confirmAttendanceBackfill = () => confirm(
        tr("manage:theDayOfTheWeekOrStartTimeHas_290"),
      );

      saveSchoolClassWithAttendanceGuard({
        originalSchoolClass: props.schoolClass,
        schoolClass: schoolClass.isRecurring ? schoolClass : { ...schoolClass, tillDate: schoolClass.fromDate },
        scheduledLessons,
        saveLessonRegister,
        deleteLessonRegister,
        saveSchoolClass,
        confirmAttendanceBackfill,
      })
        .then((savedClass) => {
          if (!mounted.current || activePrincipal.current !== principalKey) return;
          saving.current = false;
          setStatus(STATUS.VIEW);
          if (creationContext && !schoolClass.id) {
            history.replace(buildDashboardReturnRoute(creationContext, location.search, {
              kind: 'created',
              classId: savedClass.id,
              teacherId: savedClass.teacherId,
              classTypeId: savedClass.classTypeId,
              fromDate: savedClass.fromDate,
              tillDate: savedClass.tillDate,
              dayOfWeek: savedClass.dayOfWeek,
              startTime: savedClass.startTime,
            }));
            return;
          }
          if (!schoolClass.id) {
            history.push(buildManageRouteWithSchoolYear(`/manage/class/${savedClass.id}`, location.search, activeSchoolYearStartYear));
          }
        })
        .catch((error) => {
          if (!mounted.current || activePrincipal.current !== principalKey) return;
          saving.current = false;
          setErrors({...errors, saveError: error.message});
          setStatus(STATUS.SUBMITTED);
        });
    } else {
      saving.current = false;
      setStatus(STATUS.SUBMITTED);
    }
  };

  const handleRegisterUpdate = async (event) => {
    saveLessonRegister(event.lessonRegister)
      .then(() => {
        setStatus(STATUS.VIEW);
      })
      .catch((error) => {
        setErrors({...errors, saveError: error.message});
        setStatus(STATUS.SUBMITTED);
      });
  };

  const handleEdit = () => {
    setStatus(STATUS.IDLE);
  };

  const handleOpenStudentPicker = () => {
    if (!canManageClass || !schoolClass.id || addingStudentRef.current) return;
    setAddStudentError("");
    setShowSelectStudentModal(true);
  };

  const handleCloseStudentPicker = () => {
    if (addingStudentRef.current) return;
    setShowSelectStudentModal(false);
    setAddStudentError("");
  };

  const handleAddStudent = async (student) => {
    if (!canManageClass || !schoolClass.id || addingStudentRef.current) return;
    if (!studentsNotInThisClass.some(option => option.value === student.value)) return;
    addingStudentRef.current = true;
    setIsAddingStudent(true);
    setAddStudentError("");
    try {
      await addClassStudent({ schoolClassId: schoolClass.id, studentId: student.value });
      setShowSelectStudentModal(false);
      toast.success(tr("manage:extraStudentAdded_77"));
    } catch (error) {
      setAddStudentError((error && error.message) || tr("manage:couldNotAddTheStudentPleaseTryAgain_291"));
    } finally {
      addingStudentRef.current = false;
      setIsAddingStudent(false);
    }
  };

  const handleRemoveStudent = (student) => {
    const {classStudent} = student;
    removeClassStudent(classStudent)
      .then(() => {
        setShowSelectStudentModal(false);
      })
      .catch((error) => {
        setErrors({...errors, saveError: error.message});
      });
  };

  return (
    <>{confirmation}<div className="mb-5">
      { hasErrors(errors) && status === STATUS.SUBMITTED && (
        <div role="alert" className="alert alert-danger">
          {Object.keys(errors).map(key => <div key={key}><LocalizedMessage isError value={errors[key]} /></div>)}
        </div>
      )}

      <SchoolClassForm
        submitLabel={creationContext ? tr("manage:saveAndReturnToTimetable_292") : undefined}
        cancelLabel={creationContext ? tr("common:cancel") : undefined}
        classTypes={classTypes}
        teachers={teachers}
        weekdays={WEEKDAYS}
        books={books}
        id={schoolClass.id}
        teacherId={schoolClass.teacherId}
        classTypeId={schoolClass.classTypeId}
        bookId={schoolClass.bookId}
        dayOfWeek={schoolClass.dayOfWeek}
        comment={schoolClass.comment}
        startTime={schoolClass.startTime}
        isRecurring={schoolClass.isRecurring}
        fromDate={schoolClass.fromDate}
        tillDate={schoolClass.tillDate}
        touched={touched}
        errors={errors}
        onBlur={handleBlur}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onEdit={handleEdit}
        onCancel={handleCancel}
        status={status}
        dateReadOnly={dateReadOnly}
        canEdit={canManageClass}
        onAddStudent={canManageClass ? handleOpenStudentPicker : null}
      />

      <ClassStudentPickerModal
        show={showSelectStudentModal}
        options={studentsNotInThisClass}
        isSubmitting={isAddingStudent}
        error={addStudentError}
        onSelect={handleAddStudent}
        onHide={handleCloseStudentPicker}
      />

      {status === STATUS.VIEW && (
        <StudentsList
          students={studentsInThisClass}
          onViewParent={() => {}}
          onEdit={null}
          onRemove={null}
          onAddStudent={null}
          hideClientIdFallback={isTeacher}
        />
      )}
      {status !== STATUS.VIEW && schoolClass.id && canManageClass && (
        <StudentsList students={studentsInThisClass} onViewParent={() => {}} onEdit={null} onRemove={handleRemoveStudent} onAddStudent={handleOpenStudentPicker}/>
      )}

      { schoolClass.id && (
        <LessonsList
          scheduledLessons={scheduledLessons}
          students={students}
          teachers={teachers}
          classTypes={classTypes}
          lessonTypes={lessonTypes}
          onSubmit={handleRegisterUpdate}
          deleteLessonRegister={deleteLessonRegister}
          role={userRole}
          userTeacherId={userTeacherId}
        />
      )}
    </div></>
  );
}

SchoolClass.propTypes = {
  match: PropTypes.shape({ params: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired }).isRequired,
  principalKey: PropTypes.string,
  schoolClasses: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  schoolClass: PropTypes.object.isRequired,
  loadSchoolClasses: PropTypes.func.isRequired,
  saveSchoolClass: PropTypes.func.isRequired,
  loadTeachers: PropTypes.func.isRequired,
  scheduledLessons: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  loadStudents: PropTypes.func.isRequired,
  addClassStudent: PropTypes.func.isRequired,
  removeClassStudent: PropTypes.func.isRequired,
  loadClassStudents: PropTypes.func.isRequired,
  studentsInThisClass: PropTypes.array.isRequired,
  studentsNotInThisClass: PropTypes.array.isRequired,
  saveLessonRegister: PropTypes.func.isRequired,
  deleteLessonRegister: PropTypes.func.isRequired,
  loadLessonRegisters: PropTypes.func.isRequired,
  books: PropTypes.array.isRequired,
  loadBooks: PropTypes.func.isRequired,
  clients: PropTypes.array.isRequired,
  loadClients: PropTypes.func.isRequired,
  activeSchoolYearStartYear: PropTypes.number.isRequired,
  userRole: PropTypes.string,
  userTeacherId: PropTypes.string,
  getSettings: PropTypes.func,
};

SchoolClass.defaultProps = {
  principalKey: '',
  userRole: "",
  userTeacherId: "",
  getSettings: SettingsService.getSettings,
};

function getStudentsNotInThisClass(students, classStudents, schoolClassId, clients) {
  const studentIds = classStudents.filter(cs => cs.schoolClassId === schoolClassId).map(cs => cs.studentId);
  return students.filter(s => !studentIds.includes(s.id)).map(s => {
    let clientName = '';
    if (s.clientId && clients) {
      const client = clients.find(c => c.id === s.clientId);
      if (client) {
        clientName = client.firstName && client.lastName
          ? `${client.firstName} ${client.lastName}`
          : client.firstName || s.clientId;
      } else {
        clientName = s.clientId;
      }
    }
    return {
      value: s.id,
      text: clientName ? `${getName(s)} [${clientName}]` : getName(s),
      primaryText: getName(s),
      secondaryText: clientName ? `Klient: ${clientName}` : "",
    };
  });
}

const mapStateToProps = (state, ownProps) => {
  const {id} = ownProps.match.params;
  const schoolClass =
    id === "new" || state.schoolClasses.length === 0
      ? EMPTY_SCHOOL_CLASS
      : getById(state.schoolClasses, id);

  const scheduledLessons = API.generateScheduledClasses(schoolClass, state.lessonRegister, state.students, state.classStudents);

  const studentsInThisClass =
    state.students && state.classStudents && schoolClass.id
      ? getStudentsInThisClass(state.students, state.classStudents, schoolClass.id, state.clients)
      : [];
  const studentsNotInThisClass =
    state.students && state.classStudents && schoolClass.id
      ? getStudentsNotInThisClass(state.students, state.classStudents, schoolClass.id, state.clients)
      : [];

  return {
    schoolClass,
    schoolClasses: state.schoolClasses,
    teachers: state.teachers,
    students: state.students,
    books: state.books,
    scheduledLessons,
    classStudents: state.classStudents,
    studentsInThisClass,
    studentsNotInThisClass,
    lessonRegister: state.lessonRegister,
    clients: state.clients,
    activeSchoolYearStartYear: state.settings.activeSchoolYearStartYear,
    userRole: getEffectivePermissionRole(state.user),
    principalKey: getPrincipalKey(state.user),
    userTeacherId: state.user.teacherId,
  };
};

const mapDispatchToProps = {
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  saveSchoolClass: schoolClassActions.saveSchoolClass,
  loadClassStudents: classStudentsActions.loadClassStudents,
  addClassStudent: classStudentsActions.addClassStudent,
  removeClassStudent: classStudentsActions.removeClassStudent,
  loadTeachers: teacherActions.loadTeachers,
  loadStudents: studentActions.loadStudents,
  loadBooks: bookActions.loadBooks,
  saveLessonRegister: lessonRegisterActions.saveLessonRegister,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  deleteLessonRegister: lessonRegisterActions.deleteLessonRegister,
  loadClients: clientActions.loadClients,
};

export default connect(mapStateToProps, mapDispatchToProps)(SchoolClass);
